import { IntakeCard } from '../nutrition/IntakeCard'
import { MoodCard } from '../journal/MoodCard'
import { DailyMetricChange } from '@/shared/components/DailyMetricChange'
import { useState } from 'react'

import { useAppSettings } from '@/app/AppProviders'
import type { SleepRecord } from '@/domain/health/types'
import { formatWeight } from '@/domain/health/units'

import { localFieldsAt } from '../metrics/zonedDateTime'

import { AddDataDialog } from './AddDataDialog'
import { HealthCard, type HealthValue } from './HealthCard'
import { TodayStatusHeader } from './TodayStatusHeader'
import { TodaySyncPanel } from './TodaySyncPanel'
import { useTodaySnapshot } from './useTodaySnapshot'

interface TodayPageProps {
  referenceAt?: string
  timeZone?: string
}

function shortLocalDay(localDay: string): string {
  const match = /^\d{4}-(\d{2})-(\d{2})$/u.exec(localDay)
  if (match === null) return localDay
  return `${Number(match[1])}月${Number(match[2])}日`
}

function formatDecimal(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function splitValue(formatted: string): HealthValue {
  const splitAt = formatted.lastIndexOf(' ')
  return splitAt === -1
    ? { amount: formatted, unit: '' }
    : { amount: formatted.slice(0, splitAt), unit: formatted.slice(splitAt + 1) }
}

function formatSleepTimes(record: SleepRecord): string {
  const start = localFieldsAt(record.startAt, record.wakeTimeZone).localTime
  const end = localFieldsAt(record.endAt, record.wakeTimeZone).localTime
  return `${start} – ${end}`
}

export function TodayPage({ referenceAt, timeZone }: TodayPageProps) {
  const [resolvedReferenceAt] = useState(() => referenceAt ?? new Date().toISOString())
  const [resolvedTimeZone] = useState(
    () => timeZone ?? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'),
  )
  const [addOpen, setAddOpen] = useState(false)
  const { settings } = useAppSettings()
  const { snapshot, score, error, reload } = useTodaySnapshot(resolvedReferenceAt, resolvedTimeZone)

  if (error !== null) {
    return (
      <section className="today-page" aria-labelledby="today-title">
        <TodayStatusHeader referenceAt={resolvedReferenceAt} timeZone={resolvedTimeZone} />
        <div className="today-page__error" role="alert" aria-labelledby="today-error-title">
          <h2 id="today-error-title">今日数据暂时无法读取</h2>
          <p>本地数据仍安全保存在此设备，请重新读取。</p>
          <button type="button" onClick={reload}>重新读取</button>
        </div>
      </section>
    )
  }

  if (snapshot === null) {
    return (
      <section className="today-page" aria-labelledby="today-title">
        <TodayStatusHeader referenceAt={resolvedReferenceAt} timeZone={resolvedTimeZone} />
        <div
          className="health-grid health-grid--loading"
          role="status"
          aria-label="正在读取今日数据"
        >
          {Array.from({ length: 4 }, (_, index) => (
            <article
              className="health-card health-card--skeleton"
              data-testid="health-card-skeleton"
              key={index}
              aria-hidden="true"
            />
          ))}
        </div>
      </section>
    )
  }

  const { weight, bodyFat, sleep, activeEnergy } = snapshot

  return (
    <section className="today-page" aria-labelledby="today-title">
      <TodayStatusHeader referenceAt={resolvedReferenceAt} timeZone={resolvedTimeZone} />
      <TodaySyncPanel />
      <div className="health-grid">
        <HealthCard
          title="体重"
          value={
            weight === null
              ? null
              : splitValue(formatWeight(weight.record.value, settings.displayWeightUnit))
          }
          supportingText={
            weight === null
              ? '暂无体重数据'
              : weight.isHistorical
                ? `最近记录：${shortLocalDay(weight.record.localDay)}`
                : '今日记录'
          }
          href="/metrics/weight"
          change={<DailyMetricChange comparison={weight?.comparison} type="weight" unit={settings.displayWeightUnit} />}
          accent="weight"
        />
        <HealthCard
          title="体脂"
          value={
            bodyFat === null
              ? null
              : { amount: bodyFat.record.value.toFixed(1), unit: '%' }
          }
          supportingText={
            bodyFat === null
              ? '暂无体脂数据'
              : bodyFat.isHistorical
                ? `最近记录：${shortLocalDay(bodyFat.record.localDay)}`
                : '今日记录'
          }
          href="/metrics/bodyFat"
          change={<DailyMetricChange comparison={bodyFat?.comparison} type="bodyFat" unit={settings.displayWeightUnit} />}
          accent="bodyFat"
        />
        <HealthCard
          title="睡眠"
          value={
            sleep === null
              ? null
              : { amount: formatDecimal(sleep.durationMinutes / 60), unit: '小时' }
          }
          supportingText={sleep === null ? '暂无睡眠数据' : formatSleepTimes(sleep)}
          detail={score ? (
            <div className="health-card__sleep-score" aria-label={`CareBaby 睡眠评分 ${score.value}分，${score.partial ? '部分评分' : '完整评分'}，默认8小时目标`}>
              <span>评分{score.partial ? ' · 部分' : ''}</span>
              <strong>{score.value}<small>分</small></strong>
            </div>
          ) : null}
          href="/sleep"
          accent="sleep"
        />
        <HealthCard
          title="活动卡路里"
          value={
            activeEnergy === null
              ? null
              : { amount: formatDecimal(activeEnergy.value), unit: 'kcal' }
          }
          supportingText={activeEnergy === null ? '暂无活动卡路里数据' : '今日累计'}
          href="/metrics/activeEnergy"
          accent="energy"
        />
      </div>
      <IntakeCard timeZone={resolvedTimeZone} referenceAt={referenceAt} />
      <MoodCard timeZone={resolvedTimeZone} referenceAt={referenceAt} />
      <button className="today-page__add" type="button" onClick={() => setAddOpen(true)}>
        <span aria-hidden="true">＋</span>
        添加今日数据
      </button>

      <AddDataDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </section>
  )
}
